// import "./zoom-gallery";

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.activeVideos = [];

    window.loadSwiper.then((swiper) => {
      this.swiperLib = swiper;
      this._initCarousel();
    });
  }

  connectedCallback() {
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        document.body.classList.remove("no-scroll");

        if (this.gallerySwiper?.slides?.length) {
          this.gallerySwiper.zoom?.out();
        }
      }
    });

    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-video-toggle]")) {
        this.querySelector(
          ".swiper-slide-active [data-video-control-button]",
        )?.click();
        return;
      }

      if (e.target.closest("[data-video-control-button]")) return;

      const zoomButton = e.target.closest("[data-open-zoom]");
      if (zoomButton) {
        this._openZoomGallery(zoomButton, {
          view: zoomButton.dataset.openZoom,
          tab: zoomButton.dataset.zoomTab,
        });
      }
    });

    // Triggers outside the gallery — the dimensions drawing in the PDP
    // accordions, for one — ask for the modal by event, since the markup for it
    // only exists in here.
    this.handleExternalOpen = (e) => {
      if (!this._zoomMarkup) return;

      this._openZoomGallery(e.target, e.detail);
    };

    document.addEventListener("zoom-gallery:open", this.handleExternalOpen);
  }

  disconnectedCallback() {
    document.removeEventListener("zoom-gallery:open", this.handleExternalOpen);
  }

  _initCarousel() {
    const { Swiper } = this.swiperLib;

    if (this.gallerySwiper) return;

    this._toggleSwiperClasses(true);

    // The PDP gallery sits flush (0); the quickview opts into the wider gutters
    // via data attributes.
    const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 0;
    const spaceBetweenLarge =
      parseInt(this.dataset.spaceBetweenLarge, 10) || spaceBetween;

    const swiperParams = {
      slidesPerView: "auto",
      speed: 400,
      spaceBetween,
      breakpoints: {
        1024: {
          spaceBetween: spaceBetweenLarge,
        },
      },
      zoom: {
        maxRatio: 1,
      },
      watchSlidesProgress: true,
      centerInsufficientSlides: true,
      navigation: {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      },
      on: {
        realIndexChange: () => {
          this._updateCurrentSlide();
          this._trackSlideView();
        },
      },
    };

    this.gallerySwiper = new Swiper(
      this.querySelector(`.${this._prefix}__media-container`),
      swiperParams,
    );

    // Prefix-scoped rather than by element, so the zoom gallery rendered inside
    // this one can grow pips of its own without being picked up here.
    this._disconnectPips = window.connectCarouselPips(
      this.gallerySwiper,
      this.querySelector(`.${this._prefix}__pagination`),
    );

    this._updateCurrentSlide();
    this._trackSlideView();

    this.gallerySwiper.on("slideChange", () => {
      this.dispatchEvent(
        new CustomEvent("cylindo-trigger", {
          bubbles: true,
          detail: { action: null },
        }),
      );

      const visibleSlides = this.gallerySwiper.slides.filter((slide) =>
        slide.classList.contains("swiper-slide-visible"),
      );
      this._selectVideo(visibleSlides);
    });

    this.gallerySwiper.on("click", (_, e) => {
      if (e.target.closest("[data-video-control-button]")) return;

      // Overlaid controls (arrows, cylindo triggers, pagination) live inside the
      // swiper container, so only treat clicks on the media itself as a zoom.
      if (!e.target.closest(`.${this._prefix}__media-slides`)) return;

      this._openZoomGallery(this.querySelector("[data-open-zoom]"));
    });
  }

  /**
   * Syncs everything that reflects the active slide: slide accessibility, the
   * mobile slide counter and the mobile progress bar.
   */
  _updateCurrentSlide() {
    if (!this.gallerySwiper) return;

    const { realIndex, slides } = this.gallerySwiper;
    const activeSlide = slides[realIndex];

    slides.forEach((slide) => {
      if (slide === activeSlide) {
        slide.removeAttribute("aria-hidden");
        slide.querySelectorAll("a, button").forEach((el) => {
          el.tabIndex = null;
        });
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.querySelectorAll("a, button").forEach((el) => {
          el.tabIndex = -1;
        });
      }
    });

    const currentSlideLabel = this.querySelector("[data-slide-current]");
    if (currentSlideLabel) {
      currentSlideLabel.textContent = realIndex + 1;
    }

    this.querySelector(`.${this._prefix}__progress`)?.style.setProperty(
      "--slide-index",
      realIndex,
    );
  }

  /**
   * Reports the slide now in view to the data layer. Flipping back to a slide
   * counts as a fresh view, so this deliberately doesn't dedupe. Opt-in, so the
   * quickview's gallery doesn't report alongside the PDP's.
   */
  _trackSlideView() {
    if (!this.gallerySwiper || !this._tracksSlideViews) return;

    window.ElevarDataLayer?.push({
      event: "view_gallery_image",
      detail: {
        image_number: this.gallerySwiper.realIndex + 1,
      },
    });
  }

  /**
   * @param trigger {Element} Element the modal event is dispatched from, so
   *   focus returns to it on close
   * @param options {Object} How the modal should open:
   *   `view` ('grid' | 'carousel', default carousel), `tab` (tab key) and
   *   `mediaId`. Without a tab or media id, a carousel opens on the gallery's
   *   current slide.
   */
  _openZoomGallery(trigger, options = {}) {
    const zoomGallery = this._zoomMarkup?.content.children[0];

    if (!zoomGallery) return;

    const view = options.view === "grid" ? "grid" : "carousel";
    const activeSlide =
      this.gallerySwiper?.slides[this.gallerySwiper.realIndex];
    const mediaId =
      options.mediaId ||
      (view === "carousel" && !options.tab ? activeSlide?.dataset.mediaId : "");

    // The template element is reused on every open, so last time's state has to
    // be cleared rather than left to be overwritten.
    delete zoomGallery.dataset.initialTab;
    delete zoomGallery.dataset.initialMediaId;

    zoomGallery.dataset.initialView = view;
    if (options.tab) zoomGallery.dataset.initialTab = options.tab;
    if (mediaId) zoomGallery.dataset.initialMediaId = mediaId;

    const html = zoomGallery.outerHTML;
    (trigger || this).dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { html, target: "zoom-modal" },
      }),
    );
  }

  _destroyCarousel() {
    if (!this.gallerySwiper) return;

    this._disconnectPips();
    this.gallerySwiper.destroy(true, true);
    this.gallerySwiper = null;
    this._toggleSwiperClasses(false);
  }

  _toggleSwiperClasses(add) {
    this.querySelectorAll("[data-swiper-class]").forEach((el) => {
      el.classList.toggle(el.dataset.swiperClass, add);
    });
  }

  _selectVideo(els) {
    this.activeVideos.forEach((video) => video.pause());

    this.activeVideos = [];

    els.forEach((el) => {
      const video = el.querySelector("video");

      if (video) {
        this.activeVideos.push(video);
        video.play();
      }
    });
  }

  get _zoomMarkup() {
    return this.querySelector("[data-zoom-markup]");
  }

  get _prefix() {
    return this.dataset.classPrefix;
  }

  get _tracksSlideViews() {
    return this.hasAttribute("data-track-slide-views");
  }

  get _isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
